import { useMemo, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { memberDisplayName, type MemberDto } from '../../lib/work-types';
import { cn } from '../../lib/utils';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { MemberAvatar, selectedMembers } from './WorkCardMeta';

/** Owners are the initiative's assignees; any active org member can own one. */
export function OwnerPicker({
  value,
  members,
  onChange,
  disabled = false,
  className,
  label,
}: {
  value: string[];
  members: MemberDto[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  className?: string;
  /** What these owners own, when a page has more than one picker. */
  label?: string;
}) {
  const [query, setQuery] = useState('');
  const owners = selectedMembers(value, members);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const sorted = [...members].sort((left, right) => memberDisplayName(left).localeCompare(memberDisplayName(right)));
    if (!needle) return sorted;
    return sorted.filter((member) => `${memberDisplayName(member)} ${member.email ?? ''}`.toLowerCase().includes(needle));
  }, [members, query]);
  const names = owners.map((member) => memberDisplayName(member)).join(', ');

  return (
    <Popover onOpenChange={(open) => !open && setQuery('')}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={
            label
              ? owners.length
                ? `${label} owners: ${names}`
                : `Add ${label} owners`
              : owners.length
                ? `Owners: ${names}`
                : 'Add owners'
          }
          className={cn(
            'flex min-h-9 max-w-full items-center gap-2 rounded-md border px-2.5 py-1 text-left text-sm',
            !disabled && 'hover:bg-muted/50',
            disabled && 'cursor-default',
            className,
          )}
        >
          {owners.length === 0 ? (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <UserPlus className="h-4 w-4" aria-hidden />
              {disabled ? 'No owner' : 'Add owners'}
            </span>
          ) : (
            <>
              <span className="flex shrink-0 -space-x-1.5" aria-hidden>
                {owners.slice(0, 4).map((member) => (
                  <MemberAvatar key={member.user_id} member={member} className="ring-2 ring-background" />
                ))}
              </span>
              <span className="min-w-0 truncate">{names}</span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-3">
        <p className="mb-2 text-sm font-medium">Owners</p>
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search people"
          aria-label="Search people"
          className="mb-2 h-8"
        />
        <div className="max-h-64 space-y-0.5 overflow-y-auto">
          {visible.length === 0 ? (
            <p className="px-1 py-2 text-xs text-muted-foreground">No one matches.</p>
          ) : (
            visible.map((member) => {
              const checked = value.includes(member.user_id);
              return (
                <label
                  key={member.user_id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-accent"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() =>
                      onChange(checked ? value.filter((id) => id !== member.user_id) : [...value, member.user_id])
                    }
                  />
                  <MemberAvatar member={member} />
                  <span className="min-w-0 truncate">{memberDisplayName(member)}</span>
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
